import type {
  Category,
  EventId,
  EventSubtask,
  PlanEvent,
  RecurrenceRule,
} from "../../../shared/types";
import { createEventId, createPlanEvent } from "../../../shared/types";
import { sortEvents } from "../utils/event-helpers";
import {
  syncCreateEvent,
  syncUpdateEvent,
  syncDeleteEvent,
} from "../../../lib/api/db-sync";
import { track } from "../../../lib/analytics";
import {
  getEventsCache,
  setEventsCache,
  emitChange,
  saveHistory,
} from "./event-store";

export function addEvent(
  title: string,
  category: Category,
  date: string,
  startTime: string,
  endTime: string,
) {
  saveHistory();
  const event = createPlanEvent(title, category, date, startTime, endTime);
  setEventsCache(sortEvents([...getEventsCache(), event]));
  emitChange();
  syncCreateEvent(event);
  track("event_created", { category, has_recurrence: false });
}

export function deleteEvent(id: EventId) {
  saveHistory();
  setEventsCache(getEventsCache().filter((e) => e.id !== id));
  emitChange();
  syncDeleteEvent(id);
}

export function deleteEventLocal(id: EventId) {
  saveHistory();
  setEventsCache(getEventsCache().filter((e) => e.id !== id));
  emitChange();
}

export function confirmDeleteEvent(id: EventId) {
  syncDeleteEvent(id);
}

export function updateEvent(
  id: EventId,
  updates: Partial<
    Pick<
      PlanEvent,
      | "title"
      | "date"
      | "startTime"
      | "endTime"
      | "description"
      | "notes"
      | "tags"
      | "recurrence"
      | "reminderMinutes"
      | "reminderTime"
      | "reminderType"
      | "category"
      | "eventSubtasks"
      | "excludedDates"
    >
  >,
) {
  saveHistory();
  setEventsCache(
    sortEvents(
      getEventsCache().map((e) => (e.id === id ? { ...e, ...updates } : e)),
    ),
  );
  emitChange();
  syncUpdateEvent(id, { ...updates });
  track("event_updated", { fields: Object.keys(updates) });
}

export function addEventFull(data: {
  title: string;
  category: Category;
  date: string;
  startTime: string;
  endTime: string;
  allDay?: boolean;
  description: string;
  notes: string;
  tags: readonly string[];
  recurrence: RecurrenceRule | null;
  detachedFromId?: EventId;
  reminderMinutes: number | null;
  reminderTime: string | null;
  reminderType: "BEFORE" | "AT_TIME" | null;
  eventSubtasks?: readonly EventSubtask[];
}) {
  saveHistory();
  const event: PlanEvent = {
    id: createEventId(),
    createdAt: Date.now(),
    ...data,
  };
  setEventsCache(sortEvents([...getEventsCache(), event]));
  emitChange();
  syncCreateEvent(event);
  track("event_created", { category: data.category, has_recurrence: data.recurrence !== null });
  return event.id;
}

export function detachOccurrence(
  baseId: EventId,
  originalDate: string,
  data: {
    title: string;
    category: Category;
    date: string;
    startTime: string;
    endTime: string;
    description: string;
    notes: string;
    tags: readonly string[];
    reminderMinutes: number | null;
    reminderTime: string | null;
    reminderType: "BEFORE" | "AT_TIME" | null;
  },
) {
  saveHistory();
  const updated = getEventsCache().map((e) => {
    if (e.id !== baseId) return e;
    const existing = e.excludedDates ?? [];
    if (existing.includes(originalDate)) return e;
    return { ...e, excludedDates: [...existing, originalDate] };
  });
  const newEvent: PlanEvent = {
    id: createEventId(),
    createdAt: Date.now(),
    recurrence: null,
    detachedFromId: baseId,
    excludedDates: undefined,
    eventSubtasks: [],
    allDay: false,
    tags: [...data.tags],
    title: data.title,
    category: data.category,
    date: data.date,
    startTime: data.startTime,
    endTime: data.endTime,
    description: data.description,
    notes: data.notes,
    reminderMinutes: data.reminderMinutes,
    reminderTime: data.reminderTime,
    reminderType: data.reminderType,
  };
  setEventsCache(sortEvents([...updated, newEvent]));
  emitChange();
  const baseEvent = updated.find((e) => e.id === baseId);
  if (baseEvent) {
    syncUpdateEvent(baseId, { excludedDates: baseEvent.excludedDates ?? [] });
  }
  syncCreateEvent(newEvent);
}

export function addRecurrenceException(baseId: EventId, date: string) {
  saveHistory();
  setEventsCache(
    getEventsCache().map((e) => {
      if (e.id !== baseId) return e;
      const existing = e.excludedDates ?? [];
      if (existing.includes(date)) return e;
      return { ...e, excludedDates: [...existing, date] };
    }),
  );
  emitChange();
  const updated = getEventsCache().find((e) => e.id === baseId);
  if (updated) {
    syncUpdateEvent(baseId, { excludedDates: updated.excludedDates ?? [] });
  }
}

export function resyncOccurrence(
  baseId: EventId,
  date: string,
  detachedId: EventId,
) {
  saveHistory();
  setEventsCache(
    getEventsCache()
      .filter((e) => e.id !== detachedId)
      .map((e) => {
        if (e.id !== baseId) return e;
        const excluded = (e.excludedDates ?? []).filter((d) => d !== date);
        return {
          ...e,
          excludedDates: excluded.length > 0 ? excluded : undefined,
        };
      }),
  );
  emitChange();
  syncDeleteEvent(detachedId);
  const updated = getEventsCache().find((e) => e.id === baseId);
  if (updated) {
    syncUpdateEvent(baseId, { excludedDates: updated.excludedDates ?? [] });
  }
}

export function resyncAllOccurrences(baseId: EventId) {
  saveHistory();
  const detachedIds = getEventsCache()
    .filter((e) => e.detachedFromId === baseId)
    .map((e) => e.id);
  setEventsCache(
    getEventsCache()
      .filter((e) => e.detachedFromId !== baseId)
      .map((e) => (e.id === baseId ? { ...e, excludedDates: undefined } : e)),
  );
  emitChange();
  for (const detachedId of detachedIds) {
    syncDeleteEvent(detachedId);
  }
  syncUpdateEvent(baseId, { excludedDates: [] });
}
